import { DONUT_PALETTE } from "./chartPalette";
import type { GroupCount } from "@/hooks/buildings/useBuildingDocumentStats";

export interface ChartSlice extends GroupCount {
  color: string;
}

// Last palette entry (gray) is reserved for the "Other" bucket; the rest each
// colour one real slice, so MAX_SLICES entries never repeat a colour.
const SLICE_COLORS = DONUT_PALETTE.slice(0, -1);
const OTHER_COLOR = DONUT_PALETTE[DONUT_PALETTE.length - 1];

export const MAX_SLICES = SLICE_COLORS.length;

/** Donut slices: the top entries each get their own colour; everything past
 * MAX_SLICES collapses into one gray "Other" slice so a 30-entry grouping
 * (Discipline) renders a handful of readable slices instead of 30 slivers
 * with cycling colours. */
export function sliceData(data: GroupCount[], otherLabel: string): ChartSlice[] {
  const top = data.slice(0, MAX_SLICES).map((d, i) => ({ ...d, color: SLICE_COLORS[i] }));
  const otherCount = data.slice(MAX_SLICES).reduce((sum, d) => sum + d.count, 0);
  if (otherCount > 0) top.push({ name: otherLabel, count: otherCount, color: OTHER_COLOR });
  return top;
}

/** Legend entries: every entry with a count listed with the colour it has on
 * the donut — entries bucketed into "Other" carry that slice's gray dot, so
 * donut and legend always agree on what a colour means. Zero-count entries are
 * dropped: they draw no slice, so listing them only pads the legend (and eats
 * into the "+N more" budget). Colours are still assigned by the entry's
 * position in the full data, so the ones that remain keep their donut colour. */
export function legendData(data: GroupCount[]): ChartSlice[] {
  return data
    .map((d, i) => ({
      ...d,
      color: i < MAX_SLICES ? SLICE_COLORS[i] : OTHER_COLOR,
    }))
    .filter((d) => d.count > 0);
}
